import { mediaKind } from "./chapterState";
import type { WorldManifest } from "./worldManifest";

export interface ChapterPlayerOptions {
  viewportWidth?: number;
  reducedMotion?: boolean;
}

export interface ChapterPlayer {
  activate(index: number): void;
  togglePlayback(): boolean;
  setReducedMotion(value: boolean): void;
  dispose(): void;
}

export const mountChapterPlayer = (
  root: HTMLElement,
  manifest: WorldManifest,
  options: ChapterPlayerOptions = {}
): ChapterPlayer => {
  const kind = mediaKind(options.viewportWidth ?? window.innerWidth);
  let reducedMotion = options.reducedMotion ?? false;
  let activeIndex = -1;
  let pausedByVisitor = false;
  const cleanups: Array<() => void> = [];

  root.innerHTML = manifest.sections
    .map((section, index) => {
      const media = section[kind];
      return `
        <figure class="chapter-media${index === 0 ? " is-active" : ""}" data-chapter-media="${index}">
          <img src="${media.poster}" alt="" class="chapter-media__poster" />
          <video
            class="chapter-media__video"
            data-src="${media.clip}"
            muted
            playsinline
            preload="none"
            aria-hidden="true"
          ></video>
        </figure>`;
    })
    .join("");

  const layers = [...root.querySelectorAll<HTMLElement>("[data-chapter-media]")];
  const videos = [...root.querySelectorAll<HTMLVideoElement>("video")];

  videos.forEach((video, index) => {
    const onPlaying = () => layers[index].classList.add("has-painted");
    const onEnded = () => layers[index].classList.add("has-ended");
    const onError = () => layers[index].classList.add("has-error");
    video.addEventListener("playing", onPlaying);
    video.addEventListener("ended", onEnded);
    video.addEventListener("error", onError);
    cleanups.push(() => {
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("error", onError);
    });
  });

  const prepare = (index: number) => {
    const video = videos[index];
    if (!video || video.src) return;
    video.src = video.dataset.src ?? "";
    video.load();
  };

  const activate = (index: number) => {
    const next = Math.max(0, Math.min(manifest.sections.length - 1, index));
    if (next === activeIndex) return;
    const previous = activeIndex;
    activeIndex = next;
    pausedByVisitor = false;

    layers.forEach((layer, layerIndex) => {
      layer.classList.toggle("is-active", layerIndex === next);
      layer.classList.toggle("is-previous", layerIndex === previous);
    });
    videos.forEach((video, videoIndex) => {
      if (videoIndex !== next) video.pause();
    });

    [next - 1, next, next + 1].forEach(prepare);
    const activeVideo = videos[next];
    if (!reducedMotion && activeVideo) {
      if (activeVideo.ended) activeVideo.load();
      void activeVideo.play().catch(() => layers[next].classList.add("has-error"));
    }
  };

  const togglePlayback = () => {
    const video = videos[activeIndex];
    if (!video || reducedMotion) return true;
    if (video.paused) {
      pausedByVisitor = false;
      void video.play().catch(() => layers[activeIndex].classList.add("has-error"));
    } else {
      pausedByVisitor = true;
      video.pause();
    }
    return pausedByVisitor;
  };

  const setReducedMotion = (value: boolean) => {
    reducedMotion = value;
    root.classList.toggle("is-reduced-motion", value);
    if (value) videos.forEach((video) => video.pause());
    else if (activeIndex >= 0 && !pausedByVisitor) void videos[activeIndex].play().catch(() => undefined);
  };

  setReducedMotion(reducedMotion);

  return {
    activate,
    togglePlayback,
    setReducedMotion,
    dispose() {
      videos.forEach((video) => {
        video.pause();
        video.removeAttribute("src");
        video.load();
      });
      cleanups.forEach((cleanup) => cleanup());
      root.innerHTML = "";
    }
  };
};
